import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  ClipboardList,
  Search,
  Star,
  Clock,
  FileEdit,
  Loader2,
  User,
  Calendar,
  Download,
  Trash2,
} from 'lucide-react'
import { useAllIndividualEvaluations, useDeleteEvaluation } from '@/hooks/queries/useEvaluationQueries'
import { getAllIndividualEvaluations } from '@/api/performance'
import { useTenant } from '@/hooks/useTenant'
import { useAuth } from '@/hooks/useAuth'
import { MetricCard } from '@/components/MetricCard'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/EmptyState'
import { SkeletonCard } from '@/components/common/SkeletonCard'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { exportAvaliacoesToExcel } from './utils/exportToExcel'
import { usePageTitle } from '@/stores/page-title-store'

// ─── Constants ───────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'draft', label: 'Rascunho' },
  { value: 'submitted', label: 'Enviada' },
  { value: 'reviewed', label: 'Revisada' },
  { value: 'completed', label: 'Concluida' },
]

const STATUS_CONFIG: Record<string, { label: string; variant: 'default' | 'info' | 'warning' | 'success' }> = {
  draft: { label: 'Rascunho', variant: 'default' },
  submitted: { label: 'Enviada', variant: 'info' },
  reviewed: { label: 'Revisada', variant: 'warning' },
  completed: { label: 'Concluida', variant: 'success' },
}

const TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  self: { label: 'Autoavaliacao', color: 'bg-violet-500/20 text-violet-300' },
  manager: { label: 'Gestor', color: 'bg-cyan-500/20 text-cyan-300' },
  peer: { label: 'Par', color: 'bg-orange-500/20 text-orange-300' },
  subordinate: { label: 'Subordinado', color: 'bg-pink-500/20 text-pink-300' },
}

// ─── Helpers ─────────────────────────────────────────────────

const formatDate = (dateStr: string) => {
  if (!dateStr) return '\u2014'
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

const formatPeriod = (period: { startDate?: string; endDate?: string } | undefined) => {
  if (!period?.startDate) return '\u2014'
  const start = formatDate(period.startDate)
  const end = period.endDate ? formatDate(period.endDate) : '\u2014'
  return `${start} \u2014 ${end}`
}

const formatScore = (score: number | null | undefined) => {
  if (score === null || score === undefined) return '\u2014'
  return score.toFixed(1)
}

const getInitials = (name: string) => {
  const parts = name.split(' ').filter(p => p.length > 0)
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`
  if (parts.length === 1) return `${parts[0][0]}${parts[0][1] || ''}`
  return '?'
}

// ─── Component ───────────────────────────────────────────────

function AvaliacoesIndividuaisPage() {
  const navigate = useNavigate()
  const { tenantDomain } = useTenant()
  const { user } = useAuth()
  usePageTitle('Avaliações Individuais')
  const isAdmin = user?.role && ['admin', 'rh', 'analista-rh'].includes(user.role) || false
  const deleteEvaluationMutation = useDeleteEvaluation()
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Pagination state (accumulation pattern)
  const [allItems, setAllItems] = useState<any[]>([])
  const [nextToken, setNextToken] = useState<string | undefined>()
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Export state
  const [isExporting, setIsExporting] = useState(false)

  // Debounce search
  const searchTimeoutRef = React.useRef<NodeJS.Timeout>()
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(value), 300)
  }

  const { data, isLoading, isError } = useAllIndividualEvaluations({
    status: statusFilter !== 'all' ? statusFilter : undefined,
    search: debouncedSearch || undefined,
    limit: 25,
  })

  // Sync initial query data into accumulated state
  useEffect(() => {
    if (data?.items) {
      setAllItems(data.items)
      setNextToken(data.pagination?.nextToken)
    }
  }, [data])

  // Load more handler
  const handleLoadMore = useCallback(async () => {
    if (!nextToken || !tenantDomain || isLoadingMore) return
    setIsLoadingMore(true)
    try {
      const result = await getAllIndividualEvaluations(tenantDomain, {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: debouncedSearch || undefined,
        limit: 25,
        nextToken,
      })
      setAllItems(prev => [...prev, ...result.items])
      setNextToken(result.pagination?.nextToken)
    } catch (err) {
      console.error('Error loading more evaluations:', err)
    } finally {
      setIsLoadingMore(false)
    }
  }, [nextToken, tenantDomain, isLoadingMore, statusFilter, debouncedSearch])

  // Export handler — exhausts pagination then exports
  const handleExport = useCallback(async () => {
    if (!tenantDomain || isExporting) return
    setIsExporting(true)
    try {
      // Exhaust pagination to get all items
      let exportItems = [...allItems]
      let exportNextToken = nextToken

      while (exportNextToken) {
        const result = await getAllIndividualEvaluations(tenantDomain, {
          status: statusFilter !== 'all' ? statusFilter : undefined,
          search: debouncedSearch || undefined,
          limit: 100,
          nextToken: exportNextToken,
        })
        exportItems = [...exportItems, ...result.items]
        exportNextToken = result.pagination?.nextToken
      }

      await exportAvaliacoesToExcel({
        evaluations: exportItems,
        statusFilter,
      })
    } catch (err) {
      console.error('Error exporting to Excel:', err)
    } finally {
      setIsExporting(false)
    }
  }, [tenantDomain, isExporting, allItems, nextToken, statusFilter, debouncedSearch])

  const evaluations = useMemo(() =>
    [...allItems].sort((a, b) => {
      const dateA = a.updatedAt || a.createdAt || ''
      const dateB = b.updatedAt || b.createdAt || ''
      return dateB.localeCompare(dateA) // Most recently updated first
    }),
    [allItems]
  )

  // Compute stats from loaded data
  const stats = useMemo(() => {
    const total = evaluations.length
    const draft = evaluations.filter((e: any) => e.status === 'draft').length
    const submitted = evaluations.filter((e: any) => e.status === 'submitted').length
    const reviewed = evaluations.filter((e: any) => e.status === 'reviewed').length
    const completed = evaluations.filter((e: any) => e.status === 'completed').length
    const scored = evaluations.filter((e: any) => e.overallScore != null)
    const avg = scored.length > 0
      ? (scored.reduce((sum: number, e: any) => sum + e.overallScore, 0) / scored.length).toFixed(1)
      : '\u2014'
    return { total, draft, submitted, reviewed, completed, avg }
  }, [evaluations])

  const handleDeleteEvaluation = async (evaluationId: string, employeeName: string, status: string) => {
    const statusLabel = status === 'draft' ? 'Rascunho' : status === 'submitted' ? 'Registrada' : status
    const confirmed = window.confirm(
      `Tem certeza que deseja excluir a avaliação de "${employeeName}" (${statusLabel})?\n\nEsta ação não pode ser desfeita.`
    )
    if (!confirmed) return

    try {
      await deleteEvaluationMutation.mutateAsync(evaluationId)
    } catch (err: any) {
      console.error('Error deleting evaluation:', err)
    }
  }

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExport}
          disabled={isExporting || isLoading || allItems.length === 0}
          className="border border-brand-gray/20"
        >
          {isExporting ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Exportando...</>
          ) : (
            <><Download className="w-4 h-4 mr-2" /> Exportar Excel</>
          )}
        </Button>
      </div>

      {/* Stats Cards */}
      {!isLoading && evaluations.length > 0 && (
        <div className="dashboard-grid lg:grid-cols-4">
          <MetricCard
            title="Total de Avaliacoes"
            value={stats.total}
            icon={ClipboardList}
            trend="neutral"
          />
          <MetricCard
            title="Rascunhos"
            value={stats.draft}
            icon={FileEdit}
            trend="neutral"
          />
          <MetricCard
            title="Aguardando Revisao"
            value={stats.submitted}
            icon={Clock}
            trend="neutral"
          />
          <MetricCard
            title="Nota Media"
            value={stats.avg}
            icon={Star}
            trend="neutral"
          />
        </div>
      )}

      {/* Search and Filters */}
      <Card className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray" />
            <input
              type="text"
              placeholder="Buscar por colaborador..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-brand-surface border border-brand-border rounded-lg text-white placeholder-brand-gray focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={statusFilter === opt.value ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setStatusFilter(opt.value)}
                className={statusFilter !== opt.value ? 'border border-brand-gray/20' : ''}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          <SkeletonCard count={6} />
        </div>
      ) : isError ? (
        <Card className="p-12 text-center">
          <p className="text-red-400">Erro ao carregar avaliacoes. Tente novamente.</p>
        </Card>
      ) : evaluations.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="Nenhuma avaliacao individual encontrada"
          description="Ajuste os filtros para ver mais resultados"
        />
      ) : (
        <>
          <div className="grid gap-4">
            {evaluations.map((evaluation: any) => {
              const employeeName = evaluation.employeeName || `Funcionario ${evaluation.employeeId?.slice(0, 8)}`
              const evaluatorName = evaluation.evaluatorName || `Avaliador ${evaluation.evaluatorId?.slice(0, 8)}`
              const typeConfig = TYPE_CONFIG[evaluation.evaluatorType] || { label: evaluation.evaluatorType, color: 'bg-gray-500/20 text-gray-300' }
              const statusConfig = STATUS_CONFIG[evaluation.status] || { label: evaluation.status, variant: 'default' as const }

              return (
                <Card
                  key={evaluation.evaluationId}
                  className="card-hover p-6 cursor-pointer"
                  onClick={() => navigate({ to: '/performance/avaliacoes/form', search: { id: evaluation.evaluationId } })}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      {/* Avatar */}
                      <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-brand-dark">
                          {getInitials(employeeName).toUpperCase()}
                        </span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="font-display text-lg font-semibold text-white">
                            {employeeName}
                          </h3>
                          <Badge variant={statusConfig.variant}>
                            {statusConfig.label}
                          </Badge>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeConfig.color}`}>
                            {typeConfig.label}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-brand-gray">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">Avaliador: {evaluatorName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 flex-shrink-0" />
                            <span>{formatPeriod(evaluation.evaluationPeriod)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Star className="w-4 h-4 flex-shrink-0" />
                            <span>Nota: <span className="text-white font-medium">{formatScore(evaluation.overallScore)}</span></span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 flex-shrink-0" />
                            <span>Atualizado: {formatDate(evaluation.updatedAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Delete button — same guards as /performance/avaliacoes */}
                    {(isAdmin || evaluation.status === 'draft') && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteEvaluation(evaluation.evaluationId, employeeName, evaluation.status)
                        }}
                        className="p-2 text-brand-gray hover:text-error transition-colors duration-200 rounded-lg hover:bg-error/10 flex-shrink-0 ml-2"
                        title="Excluir avaliação"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </Card>
              )
            })}
          </div>

          {/* Loading More */}
          {isLoadingMore && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-brand-gray">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Carregando mais avaliacoes...</span>
            </div>
          )}

          {/* Load More Button */}
          {nextToken && !isLoadingMore && (
            <div className="flex justify-center mt-6">
              <Button variant="ghost" onClick={handleLoadMore}>
                Carregar Mais
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default withPageProtection(AvaliacoesIndividuaisPage, PAGES.PERFORMANCE_AVALIACOES_INDIVIDUAIS)
